import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { base, mainnet } from "wagmi/chains";
import type { AppKitNetwork } from '@reown/appkit/networks';
import { Attribution } from 'ox/erc8021';

// Builder code for app.qinv.io — tags transactions for Base attribution/rewards
export const BASE_DATA_SUFFIX = Attribution.toDataSuffix({ codes: ['bc_nbr06c8s'] });

// Environment validation - ensure all required variables are present
const requiredEnvVars = ['VITE_REOWN_PROJECT_ID', 'VITE_SUPABASE_URL'];
for (const envVar of requiredEnvVars) {
  if (!import.meta.env[envVar]) {
    throw new Error(`Missing required environment variable: ${envVar}`);
  }
}

// Project ID from Reown Cloud
export const projectId = import.meta.env.VITE_REOWN_PROJECT_ID;

// Environment-based configuration
const getAppUrl = (): string => {
  // CRITICAL: Always use the actual page URL for WalletConnect metadata
  // Mismatched URLs cause wallet callbacks to fail (the "Try Again" popup bug)
  //
  // The metadata.url MUST match the actual page URL for:
  // 1. WalletConnect session establishment
  // 2. Wallet callbacks after signing
  // 3. Deep link returns from mobile wallets
  //
  // Using window.location.origin ensures the URL always matches,
  // regardless of whether we're on localhost, Vercel preview, or production
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // SSR fallback (should never be reached in browser context)
  return import.meta.env.VITE_APP_URL || 'https://app.qinv.io';
};

// Application metadata with environment-based URLs
// Exported for use in createAppKit (Phase 3)
export const metadata = {
  name: 'QInvWeb3',
  description: 'Quantum Index Protocol - DeFi Index Token Platform',
  url: getAppUrl(),
  icons: [
    `${getAppUrl()}/favicon.ico`,
    `${getAppUrl()}/icon-192.png`
  ]
};


// ====================================================================
// SELECTIVE SESSION CLEANUP - Prevents connection issues while preserving active sessions
// ====================================================================
// WalletConnect and wagmi store session data in localStorage.
// Stale/corrupted sessions can cause:
// - "Connection declined" errors
// - Timeout on first connection attempt
// - Infinite loading states
// - Ghost sessions that prevent new connections
//
// IMPROVEMENT: This cleanup now ONLY removes expired/corrupted sessions.
// Active valid sessions are preserved to prevent unexpected disconnects.
// Reference: https://github.com/WalletConnect/walletconnect-monorepo/issues/4181
//

/**
 * Check if a WalletConnect session contains fork/testnet RPC URLs
 * Used to detect stale sessions from when VITE_BASE_FORK_RPC was active.
 * These sessions cause CORS errors in production because the fork RPC
 * doesn't allow cross-origin requests from app.qinv.io.
 */
function sessionHasForkRpc(sessionData: any): boolean {
  try {
    const sessions = sessionData?.session?.values;
    if (!Array.isArray(sessions)) return false;
    return sessions.some(session => {
      const allNamespaces = {
        ...(session.namespaces ?? {}),
        ...(session.requiredNamespaces ?? {}),
      };
      for (const ns of Object.values(allNamespaces) as any[]) {
        const rpcMap: Record<string, string> = ns.rpcMap ?? {};
        if (Object.values(rpcMap).some(url => /base-fork|tenderly|fork/i.test(url))) {
          return true;
        }
      }
      return false;
    });
  } catch {
    return false;
  }
}

/**
 * Check if a WalletConnect session is expired
 * @param sessionData - Parsed session data from localStorage
 * @returns true if expired, false if still valid
 */
function isSessionExpired(sessionData: any): boolean {
  try {
    // Check if session has expiry data
    const sessions = sessionData?.session?.values;
    if (!Array.isArray(sessions)) {
      // No valid session structure - consider corrupted
      return true;
    }

    // Check each session's expiry
    const now = Math.floor(Date.now() / 1000);  // Unix timestamp in seconds
    return sessions.every(session => {
      if (!session.expiry || typeof session.expiry !== 'number') {
        // No expiry data - keep session (conservative approach)
        return false;
      }
      // Session is expired if expiry time has passed
      return session.expiry < now;
    });
  } catch {
    // On error, assume expired (safe to remove)
    return true;
  }
}

/**
 * Check if localStorage value is corrupted JSON
 * @param value - Raw localStorage value
 * @returns true if corrupted, false if valid
 */
function isCorruptedData(value: string): boolean {
  try {
    JSON.parse(value);
    return false;
  } catch {
    return true;
  }
}

if (typeof window !== 'undefined') {
  try {
    /**
     * Selective cleanup: Only remove expired or corrupted sessions
     * This preserves valid active sessions and prevents unexpected disconnects
     */
    const keysToCheck = Object.keys(localStorage).filter(k =>
      k.startsWith('wc@2:client:') ||  // WalletConnect v2 sessions
      k.startsWith('wagmi.store') ||   // wagmi cache
      k.startsWith('wagmi.cache') ||   // wagmi cache (alternate key)
      k.startsWith('@appkit/') ||      // Reown AppKit sessions
      k.startsWith('W3M_') ||          // Web3Modal sessions
      k.startsWith('@w3m/')            // Web3Modal sessions (alternate)
    );

    const keysToRemove: string[] = [];
    const preservedSessions: string[] = [];

    for (const key of keysToCheck) {
      const value = localStorage.getItem(key);
      if (!value) continue;

      // Remove if corrupted
      if (isCorruptedData(value)) {
        keysToRemove.push(key);
        continue;
      }

      // For WalletConnect sessions, check expiry and stale fork RPC
      if (key.startsWith('wc@2:client:')) {
        try {
          const sessionData = JSON.parse(value);
          // Remove if expired OR if session references a fork RPC while not in fork mode
          const hasStaleFork = !import.meta.env.VITE_BASE_FORK_RPC && sessionHasForkRpc(sessionData);
          if (isSessionExpired(sessionData) || hasStaleFork) {
            keysToRemove.push(key);
          } else {
            preservedSessions.push(key);
          }
        } catch {
          // Parse failed - remove corrupted data
          keysToRemove.push(key);
        }
      }

      // For wagmi cache, check age
      if (key.startsWith('wagmi.')) {
        try {
          const cacheData = JSON.parse(value);
          const state = cacheData?.state;

          // If no state data, remove
          if (!state) {
            keysToRemove.push(key);
            continue;
          }

          // Check if cache is stale (older than 7 days)
          const cacheAge = Date.now() - (state.connections?.updatedAt || 0);
          const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days

          if (cacheAge > MAX_CACHE_AGE_MS) {
            keysToRemove.push(key);
          } else {
            preservedSessions.push(key);
          }
        } catch {
          // Parse failed - remove corrupted cache
          keysToRemove.push(key);
        }
      }

      // For AppKit/Web3Modal sessions, check expiry if present
      if (key.startsWith('@appkit/') || key.startsWith('W3M_') || key.startsWith('@w3m/')) {
        try {
          const data = JSON.parse(value);
          if (data.expiry && typeof data.expiry === 'number') {
            const now = Math.floor(Date.now() / 1000);
            if (data.expiry < now) {
              keysToRemove.push(key);
            } else {
              preservedSessions.push(key);
            }
          } else {
            // No expiry data - preserve (conservative)
            preservedSessions.push(key);
          }
        } catch {
          // Parse failed - remove corrupted
          keysToRemove.push(key);
        }
      }
    }

    // Remove identified keys
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // Don't let cleanup errors break the app
  }
}

// Network configuration — Base (chainId 8453) + Ethereum mainnet (chainId 1).
// Per ADR-0007, portfolios are identified by `(chain_id, vault_address)`; adding
// a chain here also requires adding it to `SUPPORTED_CHAIN_IDS` in `@/constants/chains`
// and seeding `token_indexes` rows with that `chain_id`.
const toAppKitNetwork = (chain: typeof base | typeof mainnet): AppKitNetwork => ({
  id: chain.id,
  name: chain.name,
  nativeCurrency: chain.nativeCurrency,
  rpcUrls: chain.rpcUrls,
  blockExplorers: chain.blockExplorers,
  testnet: false,
});

export const networks: AppKitNetwork[] = [
  toAppKitNetwork(base),
  toAppKitNetwork(mainnet),
];

// Create WagmiAdapter with Base builder code attribution
export const wagmiAdapter = new WagmiAdapter({
  projectId,
  networks,

  // Tags all transactions with the Base builder code for attribution and leaderboard visibility
  dataSuffix: BASE_DATA_SUFFIX,
});

// Export wagmi config for compatibility with existing code
// Other files may still reference 'config' from this module
export const config = wagmiAdapter.wagmiConfig;

export type Config = typeof config;
